import { AxiosError } from 'axios';
import { publicApi } from './public-client';

/**
 * LOBBERO STAYS — the STAY GUEST's client (plan S3; lobbero_be/src/stays/
 * routes/links-public.js). PUBLIC: the token in the path is the whole
 * credential, so this rides `publicApi` (no session interceptors, its own CSRF
 * echo for a signed-in person previewing a link).
 *
 * NEVER LOG A TOKEN, and never put one in a URL that leaves this origin: the
 * identity vendor brings the guest back to /s/return, which carries no token
 * (the page keeps it in sessionStorage — see stay-return-page.tsx).
 */

export type StayIdentity = 'not_started' | 'pending' | 'verified' | 'failed';

/* ── Independent Stays IS0: the in-house ID check ─────────────────────────── */

/** How the page takes an ID: on this page ('inhouse') or the S2 test fake's hosted page ('vendor'). */
export type IdFlow = 'inhouse' | 'vendor';
/** 'ocr_and_manual' = scan or type; 'manual_only' = type only (no OCR on this environment). */
export type IdScanMode = 'ocr_and_manual' | 'manual_only';
/** The automatic outcome of a recorded in-house ID (never which check failed). */
export type IdReview = 'auto_confirmed' | 'needs_review' | 'confirmed' | 'rejected';
export type IdDocType = 'passport' | 'id_card' | 'driving_license';

/** The details the guest confirms. Dates are 'YYYY-MM-DD'; countries ISO alpha-2. */
export interface IdFields {
  doc_type: IdDocType | null;
  doc_country: string | null;
  nationality: string | null;
  first_name: string | null;
  last_name: string | null;
  doc_number: string | null;
  birth_date: string | null;
  expiry_date: string | null;
}

/** The scan's answer: the fields to confirm, or "type them" (nothing kept either way). */
export type IdScanResult =
  | { ok: true; fields: IdFields; confidence: 'high' | 'low'; mrz_ok: boolean; proof: string | null; scans_left: number }
  | { ok: false; reason: string; manual: true; scans_left?: number };

export interface IdRecordBody {
  method: 'ocr' | 'manual';
  fields: IdFields;
  edited: boolean;
  proof?: string | null;
}

/** The photo's types the server reads (lobbero_be src/stays/identity/scan.js ALLOWED_MIME). */
export const ID_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'image/webp'];
/** The upload cap (6 MB, the server's MAX_IMAGE_BYTES). */
export const ID_PHOTO_MAX_BYTES = 6 * 1024 * 1024;

export interface StayConsentWords {
  version: string;
  identity_text: string;
  selfie_text: string;
}

export interface StayPartyMember {
  /** Their place in the party (lead = 0). Addressed by place, never by an id. */
  position: number;
  is_lead: boolean;
  first_name: string;
  last_initial: string;
  identity: StayIdentity;
  consent_given: boolean;
  personal_link: boolean;
}

export interface StayGuestPolicy {
  who_verifies: 'every_adult' | 'lead_guest';
  arrival_time_setter: 'host_only' | 'guest_may';
}

export interface PublicStay {
  guest_first_name: string;
  listing_name: string;
  building: { name: string; address: string; timezone: string };
  stay: {
    check_in: string;
    check_out: string;
    nights: number;
    check_in_time: string;
    check_out_time: string;
    expected_arrival_time: string | null;
  };
  party_size: number;
  adults: number | null;
  steps: { trip_confirmed: boolean; consent_given: boolean; identity: StayIdentity; completed: boolean };
  identity_attempts_left: number;
  consent: StayConsentWords;
  policy: StayGuestPolicy;
  party: StayPartyMember[] | null;
  suspended: boolean;
}

/** One adult's own page (/sg/<token>). */
export interface PublicStayGuest {
  guest_first_name: string;
  listing_name: string;
  building: { name: string; address: string; timezone: string };
  stay: { check_in: string; check_out: string; nights: number; check_in_time: string; check_out_time: string };
  steps: { consent_given: boolean; identity: StayIdentity; id_review?: IdReview | null; id_method?: 'ocr' | 'manual' | null };
  identity_attempts_left: number;
  id_scan_mode?: IdScanMode;
  id_flow?: IdFlow;
  consent: StayConsentWords;
  suspended: boolean;
}

export interface PersonalLink {
  url: string;
  message_text: string;
  whatsapp_url: string;
  shown_once: true;
}

/** Every failure: the HTTP status, the server's code and words, the field map of a 400. */
export class StayPublicError extends Error {
  status: number;
  code: string | null;
  field: string | null;
  fields: Record<string, string>;

  constructor(status: number, body: Record<string, unknown> | null, fallback: string) {
    super(body && typeof body.message === 'string' ? body.message : fallback);
    this.name = 'StayPublicError';
    this.status = status;
    this.code = body && typeof body.code === 'string' ? body.code : null;
    this.field = body && typeof body.field === 'string' && body.field ? body.field : null;
    this.fields = body && body.fields && typeof body.fields === 'object' ? (body.fields as Record<string, string>) : {};
  }
}

const call = async <T>(p: Promise<{ data: T }>, fallback: string): Promise<T> => {
  try {
    const { data } = await p;

    return data;
  } catch (e) {
    const err = e as AxiosError<Record<string, unknown>>;
    if (err && err.response) throw new StayPublicError(err.response.status, err.response.data || null, fallback);
    throw new StayPublicError(0, null, fallback);
  }
};

const lead = (token: string) => `/stay-links/${encodeURIComponent(token)}`;
const personal = (token: string) => `/stay-guest-links/${encodeURIComponent(token)}`;

export const getPublicStay = (token: string): Promise<PublicStay> =>
  call(publicApi.get<PublicStay>(lead(token)), 'Could not open your check-in.');

export const confirmTrip = (
  token: string,
  input: { expected_arrival_time?: string | null; adults?: number } = {}
): Promise<void> =>
  call(publicApi.post(`${lead(token)}/trip`, { confirm: true, ...input }), 'Could not save your dates.').then(() => undefined);

export const giveConsent = (token: string, version: string, selfie: boolean): Promise<void> =>
  call(publicApi.post(`${lead(token)}/consent`, { accept: true, version, locale: 'en', selfie }), 'Could not save your answer.').then(
    () => undefined
  );

export const startIdentity = (token: string, selfie: boolean): Promise<{ redirect_url: string }> =>
  call(publicApi.post(`${lead(token)}/identity/start`, { selfie }), "We couldn't open the ID check.");

export const addAdult = (token: string, firstName: string, lastName: string): Promise<{ party: StayPartyMember[] }> =>
  call(publicApi.post(`${lead(token)}/guests`, { first_name: firstName, last_name: lastName }), 'Could not add this person.');

export const removeAdult = (token: string, position: number): Promise<void> =>
  call(publicApi.delete(`${lead(token)}/guests/${position}`), 'Could not remove this person.').then(() => undefined);

export const personalLink = (token: string, position: number): Promise<PersonalLink> =>
  call(publicApi.post(`${lead(token)}/guests/${position}/link`, {}), 'Could not make their link.');

export const getPublicStayGuest = (token: string): Promise<PublicStayGuest> =>
  call(publicApi.get<PublicStayGuest>(personal(token)), 'Could not open your check-in.');

export const giveGuestConsent = (token: string, version: string, selfie: boolean): Promise<void> =>
  call(publicApi.post(`${personal(token)}/consent`, { accept: true, version, locale: 'en', selfie }), 'Could not save your answer.').then(
    () => undefined
  );

export const startGuestIdentity = (token: string, selfie: boolean): Promise<{ redirect_url: string }> =>
  call(publicApi.post(`${personal(token)}/identity/start`, { selfie }), "We couldn't open the ID check.");

/** The photo as raw bytes (memory only on the server; never kept). */
export const postIdPhoto = (url: string, photo: Blob): Promise<IdScanResult> =>
  call(
    publicApi.post<IdScanResult>(url, photo, { headers: { 'Content-Type': photo.type || 'image/jpeg' } }),
    "We couldn't read that photo."
  );

export const scanGuestId = (token: string, photo: Blob): Promise<IdScanResult> => postIdPhoto(`${personal(token)}/id-scan`, photo);

export const recordGuestId = (token: string, body: IdRecordBody): Promise<{ review_state: IdReview }> =>
  call(publicApi.post(`${personal(token)}/id`, body), 'Could not save your ID details.');
